import { Colors, FOCUS_RING, Icon } from '@amelio/ui-library';

import { MatrixLevelCell } from './MatrixLevelCell';
import {
    MATRIX_AVERAGE_COLUMN_WIDTH,
    MATRIX_COLUMN_GAP,
    MATRIX_LABEL_COLUMN_WIDTH,
    MATRIX_ROW_MIN_HEIGHT,
} from '../reportStyles';
import { formatAverage } from '../utils/levelScale';

import type { LevelPresentation } from '../utils/levelPresentation';
import type { CompetencyMatrixEmployee, CompetencyMatrixGroup } from '@/types/reports';
import type { MouseEvent } from 'react';

import { CustomPill } from '@/components/CustomPill/CustomPill';
import { useAmelioTranslations } from '@/hooks';
import { getLocalizedText } from '@/utils/localization';

interface MatrixGroupRowProps {
    group: CompetencyMatrixGroup;
    employees: CompetencyMatrixEmployee[];
    scale: number;
    language: string;
    isExpanded: boolean;
    isDimmed: boolean;
    onToggle: () => void;
    resolveLevelPresentation: (levelId: string | null) => LevelPresentation | null;
}

/** One competency-group row of the matrix: a chevron-and-name toggle in the sticky label column,
 * one colored pill per employee showing their average level for the group, and the group's own
 * average on the right. Other rows dim while this one is expanded, matching the prototype.
 *
 * The whole row is clickable, but only the label cell is a `button`: the pills and
 * the average are data a screen-reader user reads cell by cell, and wrapping them in the control
 * would fold them all into its single accessible name. */
export const MatrixGroupRow = ({
    group,
    employees,
    scale,
    language,
    isExpanded,
    isDimmed,
    onToggle,
    resolveLevelPresentation,
}: MatrixGroupRowProps) => {
    const { t } = useAmelioTranslations();
    const groupName = getLocalizedText(group.name);

    const toggleFromButton = (event: MouseEvent<HTMLButtonElement>) => {
        // Without this the click also reaches the row, toggling the group straight back.
        event.stopPropagation();
        onToggle();
    };

    return (
        <div
            onClick={onToggle}
            className={`${MATRIX_ROW_MIN_HEIGHT} ${MATRIX_COLUMN_GAP} flex w-full cursor-pointer items-center bg-white/50 text-left transition-opacity hover:bg-white/70 ${isDimmed ? 'opacity-40' : 'opacity-100'}`}
            data-testid={`matrix-group-row-${group.id}`}
        >
            <span
                className={`${MATRIX_LABEL_COLUMN_WIDTH} sticky left-0 flex shrink-0 items-center bg-white/95 pl-2`}
            >
                <button
                    type={'button'}
                    onClick={toggleFromButton}
                    aria-expanded={isExpanded}
                    aria-label={t(
                        isExpanded
                            ? 'reports.competencyMatrix.matrix.collapse'
                            : 'reports.competencyMatrix.matrix.expand',
                        { name: groupName },
                    )}
                    className={`${FOCUS_RING} flex min-w-0 items-center gap-2 text-left`}
                    data-testid={`matrix-group-toggle-${group.id}`}
                >
                    <Icon
                        name={'arrow-arrow-down'}
                        size={'sm'}
                        className={`transition-transform ${isExpanded ? '' : '-rotate-90'}`}
                    />

                    <span
                        className={`truncate ${isExpanded ? 'text-primary font-bold' : 'text-navy font-medium'}`}
                    >
                        {groupName}
                    </span>
                </button>
            </span>

            {employees.map((employee) => {
                const employeeGroup = employee.groups.find((entry) => entry.groupId === group.id);
                const presentation = resolveLevelPresentation(employeeGroup?.levelId ?? null);

                return (
                    <MatrixLevelCell
                        key={employee.id}
                        dataTestId={`matrix-group-cell-${group.id}-${employee.id}`}
                    >
                        {presentation ? (
                            <CustomPill color={presentation.color ?? Colors.BASE_500}>
                                {presentation.label}
                            </CustomPill>
                        ) : (
                            <span
                                className={'text-base-500 text-xs'}
                                title={t('reports.competencyMatrix.matrix.notAssessed')}
                            >
                                —
                            </span>
                        )}
                    </MatrixLevelCell>
                );
            })}

            <span
                className={`${MATRIX_AVERAGE_COLUMN_WIDTH} sticky right-0 shrink-0 bg-white/95 text-center`}
            >
                {group.average !== null ? (
                    <span className={'text-primary font-bold'}>
                        {formatAverage({ average: group.average, scale, language })}
                    </span>
                ) : (
                    <span className={'text-base-500'}>—</span>
                )}
            </span>
        </div>
    );
};
